import React, { useEffect, useState } from "react";
import { useRazorpay } from "../../../../context/RazorpayContext";
import formatDate from "../../../../common/DateFormate";
import {
  applyCoupon,
  previewPlanSummary,
  removeCoupon,
} from "../../../../services/billing";
import { toast } from "react-toastify";
import { useBilling } from "../../../../context/BillingContext";
// import { useBilling } from "../../../../context/BillingContext";

const PackageSummaryModal = ({
  packageType,
  billingCycle,
  currentPlan,
  showCoupen,
}) => {
  const { invoiceModalData, onClosePackageSummaryModal, handleCreatePlan } =
    useRazorpay();

  const [isLoading, setIsLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [summaryData, setSummaryData] = useState(
    invoiceModalData?.invoiceDetails,
  );
  const [removeCouponMsg, setRemoveCouponMsg] = useState("");
  const [couponError, setCouponError] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [showCouponField, setShowCouponField] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card"); // default
  const [billingMode, setBillingMode] = useState("autopay"); // default

  const methods =
    invoiceModalData?.planType !== "paymentMethod"
      ? summaryData?.payment_method || []
      : ["card", "upi"]; // Show all methods in payment method change flow
  const billingModes = summaryData?.billing_mode || []; // Show all methods in payment method change flow

  const hasBothMethods = methods.includes("card") && methods.includes("upi");

  const hasBothBillingModes =
    billingModes.includes("autopay") && billingModes.includes("manual");

  useEffect(() => {
    if (methods.length > 0 && !paymentMethod) {
      setPaymentMethod(methods[0]);
    }
  }, [methods, paymentMethod]);

  useEffect(() => {
    if (billingModes.length > 0) {
      setBillingMode(billingModes[0]); // first billing mode
    }
  }, [billingModes]);

  // Sync with original invoiceDetails when it changes (or when coupon is removed)
  useEffect(() => {
    if (invoiceModalData?.invoiceDetails && !appliedCoupon) {
      setSummaryData(invoiceModalData.invoiceDetails);
    }
  }, [invoiceModalData?.invoiceDetails, appliedCoupon]);

  if (!invoiceModalData?.isInvoiceDetailsModal) return null;

  const handleCreatePlanArgument = (e) => {
    const newPaymentFlowState = {
      interval: billingCycle || invoiceModalData?.interval || invoiceModalData?.invoiceDetails?.interval,
      isFreePlan: Object.keys(invoiceModalData?.invoiceDetails?.trial_plan || {}).length > 0,
      billingMode: billingMode,
      ...(appliedCoupon && couponCode && { coupon_code: couponCode }),
    };
    handleCreatePlan(
      e,
      setIsLoading,
      invoiceModalData?.planType === "paymentMethod"
        ? "generatePaymentUrl"
        : currentPlan?.subscription?.status === "suspended"
          ? "generatePaymentUrl"
          : packageType,
      currentPlan,
      null,
      paymentMethod,
      newPaymentFlowState,
    );
  };

  const handlePaymentChange = (e) => {
    setPaymentMethod(e.target.value);
  };

  const handleBillingModeChange = (e) => {
    setBillingMode(e.target.value);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    setIsApplying(true);
    setCouponError("");
    setRemoveCouponMsg("");

    try {
      const response = await applyCoupon({
        coupon_code: couponCode.trim().toUpperCase(),
      });
      const responseData = response?.data;

      if (responseData?.data?.valid) {
        const payload = {
          package_id: invoiceModalData?.invoiceDetails?.package_id,
          invoice_preview_for:
            packageType === "upgrade" ? "upgrade_plan" : "plan",
          interval: billingCycle,
          coupon_code: couponCode.trim().toUpperCase(),
        };
        const previewResponse = await previewPlanSummary(payload);
        if (previewResponse?.data?.data) {
          setSummaryData(previewResponse.data.data);
          setAppliedCoupon(couponCode.trim().toUpperCase());
          setCouponError("");
          setShowCouponField(false);
        } else {
          setCouponError("Failed to fetch updated summary.");
        }
      } else {
        setCouponError(responseData?.message || "Invalid coupon code.");
        setAppliedCoupon(null);
      }
    } catch (error) {
      setCouponError(
        error.response?.data?.message || "Failed to apply coupon.",
      );
      setAppliedCoupon(null);
    } finally {
      setIsApplying(false);
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      // const response = await removeCoupon({ package_id: invoiceModalData?.invoiceDetails?.package_id });
      // if (response?.data?.success || true) { // success check depends on API
      toast.success("Coupon removed successfully");
      setAppliedCoupon(null);
      setSummaryData(invoiceModalData?.invoiceDetails);
      setCouponCode("");
      setCouponError("");
      // }
    } catch (error) {
      toast.error("Failed to remove coupon.");
    }
  };

  const isCouponAllowed = invoiceModalData?.invoiceDetails?.items?.some(
    (item) => Number(item.price.replace(/[₹,]/g, "")) > 0,
  );

  const isPendingPayment =
    currentPlan?.subscription?.status === "pending_payment";
  const isFreePlan =
    Object.keys(invoiceModalData?.invoiceDetails?.trial_plan)?.length > 0;

  const summary = summaryData || invoiceModalData?.invoiceDetails;

  const buildTimeline = (summary) => {
    if (!summary) return [];

    const timeline = [];

    // 🟢 TRIAL CASE
    if (summary?.coupon_type === "trial") {
      timeline.push({
        type: "trial",
        label: `Starting Today To ${formatDate(summary.trial_end_date)}`,
        price: "₹0.00",
      });

      timeline.push({
        type: "billing",
        label: `Starting ${formatDate(summary?.billing_start_date)} To ${formatDate(summary?.billing_end_date)}`,
        price: summary?.items?.find((i) => !i.name.includes("Trial"))?.price,
      });

      return timeline;
    }

    // 🟡 DISCOUNT CASE
    if (summary?.coupon_type === "discount") {
      timeline.push({
        type: "discount",
        label: `Starting Today To ${formatDate(Object.keys(summary?.trial_plan).length ? summary?.billing_period.end_date : summary?.billing_end_date)}`,
        price: summary?.total,
        originalPrice: summary?.items?.find((i) => !i.name.includes("Discount"))
          ?.price,
      });

      return timeline;
    }

    //  NORMAL CASE (no coupon)
    timeline.push({
      type: "normal",
      label: `Starting Today To ${formatDate(summary?.billing_period.end_date)}`,
      price: summary?.items?.[0]?.price,
    });

    return timeline;
  };

  const timelineData = buildTimeline(summary);

  const isChangePaymentMethodFlow =
    invoiceModalData?.planType === "paymentMethod";

  const isUpgradeDowngrade = !currentPlan?.trial && currentPlan?.subscription?.status === "active" && invoiceModalData?.planType !== "paymentMethod"

  const hidePaymentSection =
    isPendingPayment || isUpgradeDowngrade || isFreePlan;

  return (
    <div className="modal d-flex align-items-center justify-content-center package_summary_overlay">
      <div
        className="modal-dialog animate__animated animate__zoomIn"
        style={{ maxWidth: "550px", width: "100%", margin: "auto" }}
      >
        <div className="modal-content border-0 gapalyze-modal shadow">
          <div className="modal-body px-4 pt-3 pb-2">
            {/* Modal cross button */}
            <button
              className="package-summary-cross-icon"
              type="button"
              onClick={onClosePackageSummaryModal}
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <div className="d-flex mb-4 align-items-center">
              <div className="gapalyze-logo-box">
                <span
                  className="fw-bold gapalyze-text-primary"
                  style={{ fontSize: "20px" }}
                >
                  G
                </span>
              </div>
              <div>
                <h5 className="mb-0 fs-6 fw-bold gapalyze-text-dark text-capitalize">
                  {!isChangePaymentMethodFlow
                    ? summary?.items?.[0]?.name
                    : "Change Payment Method"}
                </h5>
              </div>
            </div>

            {invoiceModalData?.planType !== "paymentMethod" && (
              <>
                <h6
                  className="fw-normal mb-3 gapalyze-text-dark"
                  style={{ fontSize: "15px" }}
                >
                  Upcoming charges
                </h6>

                {/* Timeline */}
                <div className="timeline-container mb-4">
                  {timelineData.map((item, index) => (
                    <div className="timeline-item" key={index}>
                      <div
                        className={`timeline-dot ${index !== 0 ? "hollow" : ""}`}
                      ></div>

                      <div className="d-flex justify-content-between align-items-start">
                        {/* LEFT */}
                        <div>
                          <span
                            className="d-block gapalyze-text-dark fw-medium"
                            style={{ fontSize: "14px" }}
                          >
                            {item.label}
                          </span>

                          {/* Coupon badge */}
                          {index === 0 && appliedCoupon && (
                            <div className="d-flex align-items-center mt-1">
                              <span className="package-summary-coupon badge bg-success bg-opacity-10 text-success me-2 fw-medium">
                                {appliedCoupon}
                                <i
                                  className="package-summary-coupon-remove-tn bi bi-x-circle text-danger"
                                  onClick={handleRemoveCoupon}
                                ></i>
                              </span>
                            </div>
                          )}
                        </div>

                        {/* RIGHT */}
                        <div className="text-end">
                          <span
                            className="d-block gapalyze-text-dark fw-bold"
                            style={{ fontSize: "14px" }}
                          >
                            {/* Show original price if discount */}
                            {item.originalPrice && (
                              <span
                                className="pricing-original-price text-decoration-line-through text-muted me-1"
                                style={{ fontSize: "13px" }}
                              >
                                {item.originalPrice}
                              </span>
                            )}
                            {Object.keys(summary?.trial_plan).length > 0 ? (
                              <span>
                                {summary.trial_plan.trial_days} Days Free Trial
                              </span>
                            ) : (
                              <>
                                {item.price}/{summary?.interval}
                              </>
                            )}
                          </span>

                          {summary?.tax_amount &&
                            summary?.tax_amount !== "₹0.00" && (
                              <span
                                className="d-block gapalyze-text-muted"
                                style={{ fontSize: "11px" }}
                              >
                                includes tax of {summary?.tax_amount}
                              </span>
                            )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <hr
                  className="my-3"
                  style={{ borderColor: "#dadce0", opacity: 1 }}
                />

                {/* Coupon Section */}
                {isCouponAllowed && showCoupen && !appliedCoupon && (
                  <div className="bg-volit rounded-3 p-2 mb-2">
                    <div
                      className="d-flex justify-content-between align-items-center package_summary_pointer"
                      onClick={() => setShowCouponField(!showCouponField)}
                      style={{ cursor: "pointer" }}
                    >
                      <p className="d-block small fw-semibold text-dark mb-0">
                        <i className="bi bi-tag me-2 text-success"></i>
                        Have a coupon code?
                      </p>
                      <i
                        className={`bi bi-chevron-${showCouponField ? "up" : "down"}`}
                        style={{
                          cursor: "pointer",
                          color: "var(--body-text-color)",
                        }}
                      ></i>
                    </div>

                    {showCouponField && (
                      <div className="mt-2">
                        <div className="d-flex gap-2">
                          <input
                            type="text"
                            value={couponCode}
                            onChange={(e) =>
                              setCouponCode(e.target.value.toUpperCase())
                            }
                            onKeyPress={(e) =>
                              e.key === "Enter" && handleApplyCoupon()
                            }
                            placeholder="ENTER CODE"
                            className="form-control flex-grow-1"
                            style={{ fontSize: "13px" }}
                          />
                          <button
                            onClick={handleApplyCoupon}
                            disabled={!couponCode.trim() || isApplying}
                            className={`btn btn-outline-primary px-3 ${isApplying ? "d-flex align-items-center" : ""}`}
                            style={{ fontSize: "13px" }}
                          >
                            {isApplying ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                ></span>
                                ...
                              </>
                            ) : (
                              "Apply"
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Feedback Messages */}
                {removeCouponMsg && (
                  <div className="alert alert-success d-flex align-items-center gap-2 mt-2 mb-2 py-2 px-3 small">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>{removeCouponMsg}</span>
                  </div>
                )}
                {couponError && (
                  <div className="alert alert-danger d-flex align-items-center gap-2 mt-2 mb-2 py-2 px-3 small">
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    <span>{couponError}</span>
                  </div>
                )}
              </>
            )}

            {!hidePaymentSection && (
              <div className="payment-section">
                {/* Billing */}
                {billingModes.length > 0 && (
                  <>
                    <p className="section-title">Billing Mode</p>
                    <div className="option-grid">
                      {billingModes.map((mode) => (
                        <label
                          key={mode}
                          className={`option-card ${billingMode === mode ? "active" : ""
                            }`}
                        >
                          <input
                            type="radio"
                            value={mode}
                            checked={billingMode === mode}
                            onChange={(e) => setBillingMode(e.target.value)}
                            hidden
                          />

                          <span>
                            {mode === "autopay" ? "Auto Pay" : "Manual Pay"}
                          </span>
                        </label>
                      ))}
                    </div>
                  </>
                )}

                {/* Payment */}
                {methods.length > 0 && (
                  <>
                    <p className="section-title mt-3">Payment Method</p>
                    <div className="option-grid">
                      {methods.map((method) => {
                        const isDisabled = billingMode === "manual";

                        return (
                          <label
                            key={method}
                            className={`option-card d-flex align-items-center justify-content-center gap-2 ${!isDisabled && paymentMethod === method ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
                          >
                            <input
                              type="radio"
                              value={method}
                              checked={!isDisabled && paymentMethod === method}
                              onChange={(e) => {
                                setPaymentMethod(e.target.value);
                              }}
                              disabled={isDisabled}
                              className="d-none"
                            />

                            <i
                              className={`bi ${method === "upi" ? "bi-phone" : "bi-credit-card"}`}
                            ></i>

                            <span>{method === "upi" ? "UPI" : "Card"}</span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Info block */}
            <h6
              className="fw-normal mb-2 gapalyze-text-dark mt-3 mx-2"
              style={{ fontSize: "14px" }}
            >
              Subscribing on Gapalyze
            </h6>
            <ul
              className="mb-2 ps-3 gapalyze-text-muted mx-2"
              style={{ fontSize: "12px", lineHeight: "1.5" }}
            >
              {summary?.info?.map((item, index) => (
                <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>

            <hr
              className="my-2"
              style={{ borderColor: "#dadce0", opacity: 1 }}
            />
            {!isChangePaymentMethodFlow && (
              <p
                className="mb-0 gapalyze-text-muted small px-1"
                style={{ fontSize: "11px", lineHeight: "1.4" }}
                dangerouslySetInnerHTML={{
                  __html: summary?.term || "",
                }}
              />
            )}
          </div>
          <div className="modal-footer border-0 px-4 pb-4 pt-1 justify-content-end">
            <button
              className="gapalyze-subscribe-btn px-4 py-2"
              onClick={handleCreatePlanArgument}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></span>
                  Processing...
                </>
              ) : isChangePaymentMethodFlow ? (
                "Change Payment Method"
              ) : (
                "Subscribe"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageSummaryModal;
